import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Accordion,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EmptyMessage } from "@components/shared";

function profileName(container) {
  const snapshot = container.profile_snapshot || {};
  return snapshot.original_name || snapshot.name || container.id;
}

function CatalogBestFitPanel({ bestFit }) {
  const { t } = useTranslation();
  if (!bestFit || !bestFit.id) return null;

  const label = bestFit.original_name || bestFit.name || bestFit.id;
  const meta = [bestFit.holland, bestFit.area].filter(Boolean).join(" · ");
  const distance =
    bestFit.distance === null || bestFit.distance === undefined
      ? null
      : Number(bestFit.distance).toFixed(4);

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap={4}>
        <Text size="xs" tt="uppercase" c="dimmed" fw={600}>
          {t("cs:catalog_best_fit")}
        </Text>
        <Text fw={700}>{label}</Text>
        {meta && (
          <Text size="sm" c="dimmed">
            {meta}
          </Text>
        )}
        {distance !== null && (
          <Text size="sm">
            {t("cs:catalog_best_fit_distance")}: {distance}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {t("cs:catalog_best_fit_hint")}
        </Text>
      </Stack>
    </Paper>
  );
}

function OpccRow({ container, calcId, onRefresh, readOnly }) {
  const { t } = useTranslation();
  const [busyKey, setBusyKey] = useState(null);

  function acceptResults() {
    setBusyKey("accept");
    axios
      .post(
        `/occupational-profile-calculation-containers/${container.id}/mark-evaluated/`
      )
      .then(() => {
        toast.success(t("updated"));
        onRefresh();
      })
      .catch((err) => toast.error(err))
      .finally(() => setBusyKey(null));
  }

  function setViewable(next) {
    setBusyKey("viewable");
    axios
      .post(
        `/occupational-profile-calculation-containers/${container.id}/viewable/`,
        { viewable: next }
      )
      .then(() => {
        toast.success(t("updated"));
        onRefresh();
      })
      .catch((err) => toast.error(err))
      .finally(() => setBusyKey(null));
  }

  function openReport(kind) {
    window.open(
      `/report-clearscreen/${calcId}/${container.id}?kind=${kind}`,
      "_blank"
    );
  }

  return (
    <Stack gap="xs">
      <Group gap="xs">
        {container.completed ? (
          <Badge size="xs" color="green" variant="light">
            completed
          </Badge>
        ) : (
          <Badge size="xs" color="gray" variant="light">
            incomplete
          </Badge>
        )}
        {container.evaluated && (
          <Badge size="xs" variant="outline">
            evaluated
          </Badge>
        )}
        {container.viewable && (
          <Badge size="xs" variant="outline">
            viewable
          </Badge>
        )}
      </Group>
      <Text size="sm">
        FPF: {container.steq_score ?? "—"}
        {container.similarity ? ` (${container.similarity})` : ""}
      </Text>
      <Text size="sm">COS: {container.cos_total ?? "—"}</Text>
      <Group gap="sm" wrap="wrap">
        {!readOnly && container.completed && !container.evaluated && (
          <Button
            size="xs"
            loading={busyKey === "accept"}
            onClick={acceptResults}
          >
            Accept Results
          </Button>
        )}
        {!readOnly && container.evaluated && (
          <Switch
            label="Viewable"
            size="sm"
            checked={!!container.viewable}
            disabled={busyKey === "viewable"}
            onChange={(event) => setViewable(event.currentTarget.checked)}
          />
        )}
        {container.completed && (
          <Button
            size="xs"
            variant="light"
            onClick={() => openReport("coach")}
          >
            Coach report
          </Button>
        )}
        {container.completed && (
          <Button
            size="xs"
            variant="light"
            onClick={() => openReport("select")}
          >
            Select report
          </Button>
        )}
      </Group>
    </Stack>
  );
}

export default function AssessmentOpccResults({
  assessment,
  organizationId,
  hideHeading = false,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(null);
  const [calc, setCalc] = useState(null);
  const [joins, setJoins] = useState([]);
  const [running, setRunning] = useState(false);

  const orgId = organizationId || assessment?.organization_id;
  const scoringId = scoring?.id;

  function loadAll() {
    if (!assessment?.id) return;
    setLoading(true);
    if (orgId) {
      axios
        .post(`/organizations/${orgId}/retrieve-occupational-profiles/`, {
          page: 0,
          page_size: 500,
        })
        .then(({ data }) => setJoins(data.response?.[0]?.data || []))
        .catch(() => setJoins([]));
    } else {
      setJoins([]);
    }

    axios
      .get(`/assessments/${assessment.id}/scoring-result/`)
      .then(({ data }) => {
        const scoringRow = data.response?.[0] || null;
        setScoring(scoringRow);
        const nestedCalcId = scoringRow?.clearscreen_calculation_id;
        if (!nestedCalcId) {
          setCalc(null);
          setLoading(false);
          return;
        }
        return axios
          .get(`/clearscreen-calculations/${nestedCalcId}/`)
          .then((calcRes) => {
            setCalc(calcRes.data.response?.[0] || null);
            setLoading(false);
          });
      })
      .catch((err) => {
        setScoring(null);
        setCalc(null);
        setLoading(false);
        toast.error(err);
      });
  }

  useEffect(() => {
    loadAll();
  }, [assessment?.id, orgId]);

  function runProfiles() {
    const profileIds = joins
      .map((row) => row.occupational_profile_id)
      .filter(Boolean);
    if (!scoringId || !orgId || profileIds.length === 0) return;
    setRunning(true);
    axios
      .post(`/scoring-results/${scoringId}/run-clearscreen-calculation/`, {
        organization_id: orgId,
        occupational_profile_ids: profileIds,
      })
      .then(() => {
        toast.success(t("updated"));
        loadAll();
      })
      .catch((err) => toast.error(err))
      .finally(() => setRunning(false));
  }

  if (loading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text size="sm">Loading…</Text>
      </Group>
    );
  }

  const containers = calc?.occupational_profile_calculation_containers || [];
  const canRun = orgId && joins.length > 0 && scoringId;

  return (
    <Stack gap="sm">
      {!hideHeading && <Title order={5}>{t("cs:profile_plural")}</Title>}
      <CatalogBestFitPanel bestFit={calc?.catalog_best_fit} />
      {containers.length === 0 && (
        <>
          <EmptyMessage
            title={t("no_results")}
            text="No occupational profile calculation on this scoring result yet."
          />
          {canRun && !readOnly && (
            <Button size="xs" loading={running} onClick={runProfiles}>
              Run occupational profiles
            </Button>
          )}
        </>
      )}
      {containers.length > 0 && (
        <Accordion variant="separated">
          {containers.map((container) => (
            <Accordion.Item key={container.id} value={String(container.id)}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="xs" gap="sm">
                  <Text fw={700} truncate>
                    {profileName(container)}
                  </Text>
                  {container.evaluated && (
                    <Badge size="xs" color="green" variant="light">
                      Accepted
                    </Badge>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <OpccRow
                  container={container}
                  calcId={calc.id}
                  onRefresh={loadAll}
                  readOnly={readOnly}
                />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Stack>
  );
}
